import React, { useState, useEffect, useRef } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Chip,
    Paper,
    IconButton,
    Tooltip,
    Divider,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    ExpandMore,
    Psychology,
    Delete,
    CheckCircle,
    Cancel
} from '@mui/icons-material';

const SectionAccordion = ({
    section,
    prompts,
    level = 0,
    onSavePrompt,
    onDeletePrompt,
    getPromptForSection,
    sections,
    isLoading
}) => {
    // Use ref to persist expanded state across re-renders
    const expandedRef = useRef(level === 0);
    const [expanded, setExpanded] = useState(expandedRef.current);
    const [promptText, setPromptText] = useState('');
    const [isActive, setIsActive] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const handleChange = (event, isExpanded) => {
        expandedRef.current = isExpanded;
        setExpanded(isExpanded);
    };

    // Get section number - could be sectionNo (for top level) or no (for subsections)
    const sectionNo = section.sectionNo || section.no || '';
    const sectionName = section.sectionName || section.name || '';
    const prompt = getPromptForSection(sectionNo);

    // Initialize prompt text and active status
    useEffect(() => {
        if (prompt) {
            setPromptText(prompt.promptText || '');
            setIsActive(prompt.isActive || false);
        } else {
            setPromptText('');
            setIsActive(false);
        }
    }, [prompt]);

    const handleDeleteClick = (e) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this prompt?')) {
            onDeletePrompt(prompt._id);
        }
    };

    const handlePromptTextBlur = async () => {
        // Only save if text has changed or if creating new prompt
        const textToSave = promptText.trim();

        // Don't save if text is empty and no prompt exists
        if (!textToSave && !prompt) {
            return;
        }

        // Don't save if nothing changed
        if (prompt && prompt.promptText === textToSave && prompt.isActive === isActive) {
            return;
        }

        setIsSaving(true);
        try {
            const formData = {
                module: 'Data Repository',
                section: sectionNo,
                promptText: textToSave,
                isActive: isActive
            };
            await onSavePrompt(formData, prompt?._id);
        } catch (error) {
            console.error('Error saving prompt:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleActiveToggle = async (event) => {
        const newActiveState = event.target.checked;
        setIsActive(newActiveState);

        // Only save immediately if prompt exists and has text
        // Otherwise, the state will be saved when user types text and blurs
        if (prompt && promptText.trim()) {
            setIsSaving(true);
            try {
                const formData = {
                    module: 'Data Repository',
                    section: sectionNo,
                    promptText: promptText.trim(),
                    isActive: newActiveState
                };
                await onSavePrompt(formData, prompt._id);
            } catch (error) {
                console.error('Error updating prompt status:', error);
                // Revert on error
                setIsActive(prompt.isActive);
            } finally {
                setIsSaving(false);
            }
        }
        // If no prompt exists yet, the active state will be saved when text is entered and blurred
    };

    // Get subfolders as array, sorted by key
    const getSubfolders = (subFolders) => {
        if (!subFolders) return [];
        return Object.keys(subFolders)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(key => ({
                key,
                ...subFolders[key]
            }));
    };

    const subfolders = getSubfolders(section.subFolders);

    const indentLevel = level * 2;
    const backgroundColor = level === 0
        ? '#f8f9fa'
        : level === 1
            ? '#ffffff'
            : '#fafafa';

    const borderColor = level === 0
        ? '#4A67FF'
        : level === 1
            ? '#e0e0e0'
            : '#f0f0f0';

    return (
        <Accordion
            expanded={expanded}
            onChange={handleChange}
            sx={{
                mb: 1,
                boxShadow: level === 0 ? '0 2px 8px rgba(0,0,0,0.1)' : '0 1px 4px rgba(0,0,0,0.05)',
                borderRadius: '8px',
                overflow: 'hidden',
                '&:before': {
                    display: 'none'
                },
                backgroundColor: backgroundColor,
                borderLeft: level > 0 ? `4px solid ${borderColor}` : 'none',
                marginLeft: `${indentLevel}px`
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: level === 0 ? '#4A67FF' : '#666' }} />}
                sx={{
                    minHeight: 64,
                    '&.Mui-expanded': {
                        minHeight: 64
                    },
                    backgroundColor: level === 0 ? 'linear-gradient(135deg, #4A67FF 0%, #3956F0 100%)' : 'transparent',
                    color: level === 0 ? 'white' : '#002d63',
                    '&:hover': {
                        backgroundColor: level === 0 ? 'linear-gradient(135deg, #3956F0 0%, #2d45d0 100%)' : '#f5f5f5'
                    },
                    transition: 'all 0.2s ease'
                }}
            >
                <Box className="df jcsb aic" sx={{ width: '100%', pr: 2 }}>
                    <Box className="df aic" sx={{ gap: 2, flex: 1 }}>
                        <Psychology
                            sx={{
                                fontSize: level === 0 ? 28 : 20,
                                color: level === 0 ? '#4A67FF' : '#4A67FF'
                            }}
                        />
                        <Box>
                            <Box className="df aic" sx={{ gap: 1, mb: 0.5 }}>
                                <Typography
                                    variant={level === 0 ? 'h6' : 'subtitle1'}
                                    sx={{
                                        fontWeight: 600,
                                        color: level === 0 ? '#002d63' : '#002d63'
                                    }}
                                >
                                    {sectionNo} - {sectionName}
                                </Typography>
                                {prompt && (
                                    <Chip
                                        icon={prompt.isActive ? (
                                            <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
                                        ) : (
                                            <Cancel sx={{ fontSize: 16, color: 'white' }} />
                                        )}
                                        label={prompt.isActive ? 'Active' : 'Inactive'}
                                        size="small"
                                        sx={{
                                            backgroundColor: prompt.isActive ? '#4caf50' : '#9e9e9e',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            height: '20px',
                                            ml: 1
                                        }}
                                    />
                                )}
                            </Box>
                            {prompt && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: level === 0 ? 'rgba(255,255,255,0.8)' : '#666',
                                        display: 'block',
                                        maxWidth: '600px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {prompt.promptText.substring(0, 100)}...
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box className="df aic" sx={{ gap: 1 }}>
                        {prompt && (
                            <Tooltip title="Delete Prompt">
                                <IconButton
                                    size="small"
                                    onClick={handleDeleteClick}
                                    sx={{
                                        color: level === 0 ? 'white' : '#f44336',
                                        '&:hover': {
                                            backgroundColor: level === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(244, 67, 54, 0.1)'
                                        }
                                    }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {isSaving && (
                            <Typography variant="caption" sx={{ color: level === 0 ? 'rgba(255,255,255,0.8)' : '#666' }}>
                                Saving...
                            </Typography>
                        )}
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, pt: 1 }}>
                {/* Inline editable prompt field */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        mt: 1,
                        mb: 2,
                        borderRadius: '8px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <Box className="df jcsb aic" sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#002d63' }}>
                            Prompt {prompt ? '(Click to edit)' : '(Enter prompt text)'}
                        </Typography>
                        {prompt && (
                            <Chip
                                icon={prompt.isActive ? (
                                    <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
                                ) : (
                                    <Cancel sx={{ fontSize: 16, color: 'white' }} />
                                )}
                                label={prompt.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                    backgroundColor: prompt.isActive ? '#4caf50' : '#9e9e9e',
                                    color: 'white',
                                    fontWeight: 600
                                }}
                            />
                        )}
                    </Box>

                    {/* Active/Inactive Toggle */}
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isActive}
                                    onChange={handleActiveToggle}
                                    disabled={isSaving || isLoading}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#4caf50',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#4caf50',
                                        }
                                    }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                        {isActive ? 'Active' : 'Inactive'}
                                    </Typography>
                                    {!promptText.trim() && (
                                        <Typography variant="caption" color="textSecondary">
                                            Enter text to save prompt
                                        </Typography>
                                    )}
                                </Box>
                            }
                        />
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Editable prompt text field */}
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        onBlur={handlePromptTextBlur}
                        placeholder="Enter your prompt text here... The prompt will be saved automatically when you click outside this field."
                        disabled={isSaving || isLoading}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                                '&:hover': {
                                    backgroundColor: '#fafafa',
                                },
                                '&.Mui-focused': {
                                    backgroundColor: '#fff',
                                },
                                '& fieldset': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#4A67FF',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#4A67FF',
                                }
                            }
                        }}
                    />
                    {isSaving && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                            Saving...
                        </Typography>
                    )}
                </Paper>

                {/* Show subsections */}
                {subfolders.length > 0 ? (
                    <Box className="df fdc" sx={{ gap: 1, mt: 1 }}>
                        {subfolders.map((subfolder) => (
                            <SectionAccordion
                                key={subfolder.no || subfolder.key}
                                section={subfolder}
                                prompts={prompts}
                                level={level + 1}
                                onSavePrompt={onSavePrompt}
                                onDeletePrompt={onDeletePrompt}
                                getPromptForSection={getPromptForSection}
                                sections={sections}
                                isLoading={isLoading}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center', color: '#999' }}>
                        <Typography variant="body2">
                            No subsections
                        </Typography>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default SectionAccordion;
